import * as THREE from "three";

/*
 * The arrow in flight.
 *
 * **Presentation of a fact the simulation has already resolved**, and that
 * sentence is the whole design of this file (Phase 7's cross-cutting rule:
 * *"combat consequence never lives host-side"*). By the time anything here is
 * drawn the command has been queued, the accuracy roll is the sim's business,
 * and what the shaft is flying towards is the tile the simulation was told the
 * target is standing on. **Nothing here decides whether it hit** — there is no
 * collision test, no damage number, and no verdict; the arrow travels, arrives,
 * and disappears, and what it *did* is read off the world afterwards like every
 * other consequence in this client.
 *
 * The shape is `render/highlight.ts`'s exactly, for its reasons: one object for
 * the whole session, moved rather than rebuilt, `matrixAutoUpdate` off because
 * the matrix is written by hand once a frame. A geometry allocated per shot
 * would cost more than the shot.
 *
 * **One arrow at a time**, which is not a limitation worth apologising for: the
 * simulation's own cooldown is a shot per sixty ticks and a flight lasts a
 * fraction of that, so a second arrow in the air is a state the pair of hands
 * this client draws for cannot reach. A relaunch simply re-aims the one shaft,
 * which is also what makes the object reusable at all.
 */

/** How long a shaft takes to arrive, in seconds. Taste, and deliberately quick. */
const FLIGHT_SECONDS = 0.28;

/** How long a spent shaft lies visible where it landed, in seconds. */
const LINGER_SECONDS = 0.2;

/** The shaft's length and thickness, in tiles. */
const LENGTH = 0.7;
const THICKNESS = 0.045;

/** Wood, dark enough to read against a bright street. Chrome, not art. */
const COLOR = 0x4a3826;

/** How high above a body's feet a shaft is loosed at and aimed. */
const CHEST = 1.1;

/** A point in the world, in tiles. */
export interface FlightPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export class ArrowFlight {
  private readonly shaft: THREE.Mesh;
  private from: FlightPoint = { x: 0, y: 0, z: 0 };
  private to: FlightPoint = { x: 0, y: 0, z: 0 };

  /** Seconds since the shaft left the string, or `null` while nothing is in the air. */
  private elapsed: number | null = null;

  constructor(scene: THREE.Scene) {
    // A thin box rather than a cylinder: it is four tiles of screen at most, it
    // is seen for a quarter of a second, and a box is eight vertices.
    const geometry = new THREE.BoxGeometry(THICKNESS, THICKNESS, LENGTH);
    const material = new THREE.MeshBasicMaterial({ color: COLOR });

    this.shaft = new THREE.Mesh(geometry, material);
    this.shaft.name = "arrow in flight";
    this.shaft.matrixAutoUpdate = false;
    this.shaft.visible = false;

    scene.add(this.shaft);
  }

  /**
   * Sends a shaft from one point to another.
   *
   * Called when the command has **already been queued** — the client does not
   * ask whether the shot will land, because the answer is not host-side and
   * asking would be the beginning of a second combat model.
   *
   * Both points are feet; the shaft is drawn from chest to chest, which is the
   * one piece of presentation this file allows itself and is the same standing
   * offset the name plates use.
   */
  launch(from: FlightPoint, to: FlightPoint): void {
    this.from = from;
    this.to = to;
    this.elapsed = 0;
    this.shaft.visible = true;
  }

  /**
   * Advances the flight by a frame's worth of time.
   *
   * Frame time rather than sim ticks, and deliberately: this is presentation,
   * it must not stutter when the fixed step does, and it changes nothing a
   * replay can see. A flight that has arrived lingers for
   * {@link LINGER_SECONDS} so that a shot is visible even when the frame it
   * arrives on is the frame the target falls over on.
   */
  update(seconds: number): void {
    if (this.elapsed === null) {
      return;
    }

    this.elapsed += seconds;

    if (this.elapsed >= FLIGHT_SECONDS + LINGER_SECONDS) {
      this.hide();

      return;
    }

    const travel = Math.min(this.elapsed / FLIGHT_SECONDS, 1);
    const x = this.from.x + (this.to.x - this.from.x) * travel;
    const y = this.from.y + (this.to.y - this.from.y) * travel;
    const z = this.from.z + (this.to.z - this.from.z) * travel;

    this.shaft.position.set(x, y, z + CHEST);
    // Pointed the way it is going. `lookAt` writes a rotation and the matrix is
    // composed from it once, here, because `matrixAutoUpdate` is off.
    this.shaft.lookAt(this.to.x, this.to.y, this.to.z + CHEST);
    this.shaft.updateMatrix();
  }

  /** Takes the shaft out of the air: a session ending, a screen opening. */
  hide(): void {
    this.elapsed = null;
    this.shaft.visible = false;
  }

  /** Whether a shaft is in the air right now. The self-checks' one read. */
  get flying(): boolean {
    return this.elapsed !== null;
  }
}
